import { SOCKETIO_RECONNECT_INTERVAL } from '../../../constants';
import { getAccessTokenAsync } from '../../core/auth/utils';
import { eventEmitter } from '../../services/event-emitter-service';
import { SOCKET_EVENTS } from '../../modules/Feeds/constants';

const URL = process.env.REACT_APP_MERCURY_URL;
const COMMUNICATION_NAMESPACE_URL = `${URL}/communication`;
const { io } = require('socket.io-client');

let communicationSocket = null;
let pendingRooms = []; // Store rooms to join after connection

const callbacks = {};

/** Stable key so array room lists dedupe across calls (new array instances). */
function pendingRoomKey(roomId) {
  if (Array.isArray(roomId)) {
    return `arr:${roomId
      .slice()
      .sort((a, b) =>
        String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'variant' }),
      )
      .join('\u0001')}`;
  }
  return `id:${roomId}`;
}

function waitForCommunicationSocketConnect() {
  return new Promise((resolve) => {
    if (!communicationSocket) {
      resolve();
      return;
    }
    if (communicationSocket.connected) {
      resolve();
      return;
    }
    communicationSocket.once('connect', resolve);
  });
}

function establishCommunicationSocketConnection(token) {
  // If socket exists but is disconnected, clean it up
  if (communicationSocket) {
    communicationSocket.removeAllListeners();
    communicationSocket.disconnect();
    communicationSocket = null;
  }

  communicationSocket = io(COMMUNICATION_NAMESPACE_URL, {
    extraHeaders: {
      Authorization: `Bearer ${token}`,
    },
    autoConnect: true,
    reconnection: true,
    reconnectionAttempts: 5,
    reconnectionDelay: SOCKETIO_RECONNECT_INTERVAL,
    timeout: 20000,
  });

  setupSocketEvents();
}

function setupSocketEvents() {
  communicationSocket.on('connect', function () {
    rejoinPendingRooms();
  });

  communicationSocket.on('disconnect', function () {
    // Don't set communicationSocket to null here as it might reconnect automatically
  });

  communicationSocket.on('connect_error', function (error) {
    console.error('Communication Socket connection error:', error);
  });

  communicationSocket.on('reconnect', function () {
    rejoinPendingRooms();
  });

  communicationSocket.on('reconnect_error', function (error) {
    console.error('Communication Socket reconnection error:', error);
  });

  communicationSocket.on('reconnect_failed', function () {
    console.error('Communication Socket reconnection failed');
    // Reset the socket instance so a new connection can be established
    communicationSocket = null;
  });

  communicationSocket.on(SOCKET_EVENTS.COMMUNICATION_EVENT, (data) => {
    eventEmitter.emit(SOCKET_EVENTS.COMMUNICATION_EVENT, data);
  });
}





export async function joinMeetingRoom(roomId) {
  const roomData = {
    roomId,
  };

  const key = pendingRoomKey(roomId);
  const alreadyPending = pendingRooms.some((room) => pendingRoomKey(room.roomId) === key);
  if (!alreadyPending) {
    pendingRooms.push(roomData);
  }

  // Only create a new Socket.IO client when none exists. If one is still
  // connecting, reuse it — otherwise every caller tears down the handshake
  // and opens another WebSocket (login / re-renders amplify this badly).
  if (!communicationSocket) {
    const token = await getAccessTokenAsync();
    if (!token) {
      return;
    }
    establishCommunicationSocketConnection(token);
    await waitForCommunicationSocketConnect();
  } else if (!communicationSocket.connected) {
    await waitForCommunicationSocketConnect();
  }

  if (communicationSocket && communicationSocket.connected) {
    joinRoom(roomData.roomId);
  }
}

// Helper function to rejoin all pending rooms
function rejoinPendingRooms() {
  if (pendingRooms.length > 0) {
    const roomsToJoin = [...pendingRooms]; // Create a copy
    pendingRooms = []; // Clear the pending rooms array

    // Join all pending rooms
    roomsToJoin.forEach((roomData) => {
      joinRoom(roomData.roomId);
    });
  }
}

// Helper function to handle the actual room joining logic
function joinRoom(roomId) {
  if (!roomId) {
    //IF ROOMID IS NOT SPECIFIED THEN DEFAULT IT TO SOCKET.ID
    roomId = communicationSocket.id;
  }
  communicationSocket.emit('message', {
    action: 'join_room',
    room_id: roomId,
  });
}





export function registerAction(callback) {
  callbacks[callback.event] = callback.action;
}

export function removeAction(event) {
  callbacks[event] = false;
}

export function disconnectCommunicationSocket() {
  if (communicationSocket) {
    communicationSocket.removeAllListeners();
    communicationSocket.disconnect();
    communicationSocket = null;
  }
}
